/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { API_URL, ROLES } from '../../../../utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import SuppliersApi from '../../../../services/api/SuppliersApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import CheckboxInputComponent from '~/src/components/common/forms/CheckboxInput/CheckboxInputComponent';
import { uniq } from 'lodash';
import { ContractorUniquenessRequestDto } from '~/src/types/GetContractorDuplicateDto';
import { ContractorUniquenessResponseDto } from '~/src/types/ContractorDuplicateInfoDto';
import ContractorsApi from '~/src/services/api/ContractorsApi';
import DuplicateCheckModal from '~/src/components/common/contractors/DuplicateCheckModal';
import AutocomplitePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';
import RolesDependent from '~/src/components/common/RolesDependent';

export default function SupplierEdit(): JSX.Element {
  const { id } = useParams();
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemData, setItemData] = useState({
    name: '',
    countryId: '',
    uniqueCompanyNumber: '',
    shipmentId: '',
    svilaClientId: '',
    dataArrived: false,
  });
  const history = useHistory();
  const [modalMessage, setModalMessage] = useState('');

  const uniquenessCheck = async (values: ContractorUniquenessRequestDto, setFieldValue: any) => {
    values.entityType = 'Supplier';
    let result = null;

    if (id) {
      result = await ContractorsApi.isUniqueForUpdate('Supplier', id, values);
    } else {
      result = await ContractorsApi.isUniqueForCreate(values);
    }

    if (result?.status === StatusCodes.OK) {
      const resData: ContractorUniquenessResponseDto = result.data;
      setModalMessage(resData.message);
      setIsModalOpen(true);

      if (resData.isPossibleToCreateContractor) {
        setIsSaveButtonEnabled(true);
      } else {
        setIsSaveButtonEnabled(false);
      }

      setFieldValue('contractorId', resData.contractorId);
    }
  }

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let result = null;
    if (id) {
      result = await SuppliersApi.update(id, values);
    } else {
      result = await SuppliersApi.create(values);
    }
    if (result.status === StatusCodes.OK) {
      history.push('/suppliers');
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async () => {
    const result = await SuppliersApi.get(id);
    const newItemData = { ...result.data, dataArrived: true, uniqueCompanyNumber: result.data?.contractor?.uniqueCompanyNumber };
    setItemData(newItemData);
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  return (
    <WhitePad>
      <TitleComponent
        onLeftHandler={() => {
          history.push(`/suppliers`);
        }}
        title={`${id ? 'Edit ' : 'Create '} Supplier`}
      />
      {id && itemData.dataArrived === false ? null : (
        <Formik
          initialValues={{
            name: itemData.name || '',
            countryId: itemData.contractor?.country?.id || '',
            uniqueCompanyNumber: itemData.uniqueCompanyNumber || '',
            contractorId: '',
            nonReeferTransportCostPerPallet: itemData.nonReeferTransportCostPerPallet || 0,
            reeferTransportCostPerPallet: itemData.reeferTransportCostPerPallet || 0,
            shipmentId: itemData?.shipmentType?.id || '',
            shipmentTypeName: itemData?.shipmentType?.name || '',
            usesContainer: itemData?.shipmentType?.usesContainer || false,
            usesPallets: itemData?.shipmentType?.usesPallets || false,
            logisticsAtSource: itemData.logisticsAtSource || 0,
            freight: itemData.freight || 0,
            logisticsAtDestination: itemData.logisticsAtDestination || 0,
            customsClearance: itemData.customsClearance || 0,
            svilaClientId: itemData.svilaClientId || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            return saveItem(values, setSubmitting);
          }}
          style={{ width: '440px' }}
        >
          {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, errors }) => (
            <FormWrapper>
              <form onSubmit={handleSubmit} autoComplete="off" style={{ width: '440px' }}>
                <FormRow>
                  <TextInputComponent
                    name="name"
                    label="Name"
                    value={values.name}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      handleChange(event);
                      setIsSaveButtonEnabled(false);
                    }}
                    cols={12}
                  />
                </FormRow>
                <FormRow>
                  <AutocompleteComponent
                    url={`${API_URL}/countries/get`}
                    name="countryId"
                    initialValue={values.countryId}
                    onChange={(name, value) => {
                      setFieldValue(name, value);
                      setIsSaveButtonEnabled(false);
                    }}
                    getOptionLabel={(option) => `${option.name}`}
                    errorMessage={errors.countryId}
                    cols={12}
                    label="Country"
                  />
                </FormRow>
                <FormRow>
                  <TextInputComponent
                    name="uniqueCompanyNumber"
                    label="Unique Company Number (VAT)"
                    value={values.uniqueCompanyNumber}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      handleChange(event);
                      setIsSaveButtonEnabled(false);
                    }}
                    cols={12}
                  />
                </FormRow>
                <FormRow>
                  <AutocompleteComponent
                    url={`${API_URL}/shipment`}
                    name="shipmentId"
                    initialValue={values.shipmentId}
                    onChange={(name, value) => {
                      setFieldValue(name, value);
                      if (value == 1) {
                        setFieldValue('usesContainer', true);
                        setFieldValue('usesPallets', false);
                      } else if (value == 2) {
                        setFieldValue('usesPallets', true);
                        setFieldValue('usesContainer', false);
                      } else {
                        setFieldValue('usesContainer', false);
                        setFieldValue('usesPallets', false);
                      }
                    }}
                    getOptionLabel={(option) => `${option.name}`}
                    errorMessage={errors.shipmentId}
                    cols={12}
                    label="Type of Shipment"
                  />
                </FormRow>
                {values.usesContainer && (
                  <FormRow>
                    <AutocompleteComponent
                      url={`${API_URL}/products/containers/get`}
                      name="containerType"
                      initialValue={values.name}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      errorMessage={values.containerError}
                      cols={12}
                      label="Containers"
                    />
                  </FormRow>
                )}

                {values.usesPallets && (
                  <FormRow>
                    <AutocompleteComponent
                      url={`${API_URL}/products/pallets/get`}
                      name="truckType"
                      initialValue={values.name}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      errorMessage={errors.palletError}
                      cols={12}
                      label="Pallets"
                    />
                  </FormRow>
                )}
                <FormRow>
                  {!values.usesPallets && values.usesContainer && (
                    <TextInputComponent
                      name="logisticsAtSource"
                      label="Logistics At Source Per Container "
                      value={values.logisticsAtSource}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                  {!values.usesContainer && values.usesPallets && (
                    <TextInputComponent
                      name="logisticsAtSource"
                      label="Logistics At Source Per Pallet"
                      value={values.logisticsAtSource}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                </FormRow>
                <FormRow>
                  {!values.usesPallets && values.usesContainer && (
                    <TextInputComponent
                      name="freight"
                      label="Freight Per Container"
                      value={values.freight}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                  {!values.usesContainer && values.usesPallets && (
                    <TextInputComponent
                      name="freight"
                      label="Freight Per Pallet"
                      value={values.freight}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                </FormRow>
                <FormRow>
                  {!values.usesPallets && values.usesContainer && (
                    <TextInputComponent
                      name="logisticsAtDestination"
                      label="Logistics At Destination Per Container"
                      value={values.logisticsAtDestination}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                  {!values.usesContainer && values.usesPallets && (
                    <TextInputComponent
                      name="logisticsAtDestination"
                      label="Logistics At Destination Per Pallet"
                      value={values.logisticsAtDestination}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                </FormRow>
                <FormRow>
                  {!values.usesPallets && values.usesContainer && (
                    <TextInputComponent
                      name="customsClearance"
                      label="Customs Clearance Per Container"
                      value={values.customsClearance}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                  {!values.usesContainer && values.usesPallets && (
                    <TextInputComponent
                      name="customsClearance"
                      label="Customs Clearance Per Pallet"
                      value={values.customsClearance}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  )}
                </FormRow>
                <FormRow>
                  <TextInputComponent
                    name="nonReeferTransportCostPerPallet"
                    label="Non-Reefer Transport Cost (EUR)"
                    nonReeferTransportCostPerPallet="nonReeferTransportCostPerPallet"
                    value={values.nonReeferTransportCostPerPallet}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={12}
                  />
                </FormRow>
                <FormRow>
                  <TextInputComponent
                    name="reeferTransportCostPerPallet"
                    label="Reefer Transport Cost (EUR)"
                    reeferTransportCostPerPallet="reeferTransportCostPerPallet"
                    value={values.reeferTransportCostPerPallet}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={12}
                  />
                </FormRow>
                <RolesDependent
                  roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}>
                  <FormRow>
                    <AutocomplitePaginatedComponent
                      url={`${API_URL}/svila-clients/get-all`}
                      name="svilaClientId"
                      initialValue={values.svilaClientId}
                      onChange={(name, value) => {
                        setFieldValue(name, value);
                      }}
                      getOptionLabel={(option) => `${option.name}`}
                      errorMessage={errors.svilaClientId}
                      cols={12}
                      label="Svila Client"
                    />
                  </FormRow>
                </RolesDependent>
                <FormRow>
                  <PrimaryButton
                    variant="primary"
                    isSubmitting={isSubmitting}
                    type="button"
                    title="Check for duplicates"
                    onClick={() => uniquenessCheck(values, setFieldValue)}
                    disabled={!values.name || !values.countryId || !values.uniqueCompanyNumber}
                  />
                  <PrimaryButton
                    variant="primary"
                    type="submit"
                    isSubmitting={isSubmitting}
                    title="Save"
                    disabled={!isSaveButtonEnabled} />
                </FormRow>
              </form>
            </FormWrapper>
          )}
        </Formik>
      )
      }
      <DuplicateCheckModal
        isOpen={isModalOpen}
        message={modalMessage}
        onClose={() => setIsModalOpen(false)}
      />
    </WhitePad >
  );
}
